import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, hasLivePlayerMfa, playerFetch } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import { keys } from '@/api/hooks/shared/keys'
import { DEMO_CLIENT_CONFIG, type ClientConfigAdminView, type ClientConfigPolicy } from './types'

async function loadAdminConfig(): Promise<ClientConfigAdminView> {
  if (!hasLivePlayerMfa()) return DEMO_CLIENT_CONFIG
  try {
    return await playerFetch<ClientConfigAdminView>('/v1/admin/client-config')
  } catch (err) {
    if (isUnreachableApiError(err) || (err instanceof ApiError && err.status === 503)) {
      return DEMO_CLIENT_CONFIG
    }
    throw err
  }
}

export function useClientConfigAdmin() {
  return useQuery({
    queryKey: keys.clientConfigAdmin,
    queryFn: loadAdminConfig,
    staleTime: 15_000,
  })
}

export function useSaveClientConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (policy: ClientConfigPolicy) => {
      if (!hasLivePlayerMfa()) {
        throw new ApiError(503, 'Player-API admin is not configured', null)
      }
      return playerFetch<ClientConfigAdminView>('/v1/admin/client-config', {
        method: 'PUT',
        body: JSON.stringify(policy),
      })
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.clientConfigAdmin, data)
    },
  })
}

export function useResetClientConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (!hasLivePlayerMfa()) {
        throw new ApiError(503, 'Player-API admin is not configured', null)
      }
      return playerFetch<ClientConfigAdminView>('/v1/admin/client-config', {
        method: 'DELETE',
      })
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.clientConfigAdmin, data)
    },
  })
}
